import { Injectable, signal, WritableSignal } from '@angular/core';
import { ButtonActionEntity, ButtonCreationEntity, LinkedEntity, StatusEntity } from '../models';
import { DETAIL_ACTION_SUBJECT } from '../enums';

interface SubjectMapType {
  CREATION_BUTTON: WritableSignal<ButtonCreationEntity | undefined>;
  LINKED_BUTTON: WritableSignal<LinkedEntity | undefined>;
  STATUS_BUTTON: WritableSignal<StatusEntity | undefined>;
}

function createInitialDetailActionState() {
  return {
    creationButton: undefined as ButtonCreationEntity | undefined,
    actionMenuItems: undefined as ButtonActionEntity[] | undefined,
    linkedButton: undefined as LinkedEntity | undefined,
    status: undefined as StatusEntity | undefined,
  };
}

@Injectable({ providedIn: 'root' })
export class DetailActionService {
  private get _initial() {
    return createInitialDetailActionState();
  }

  creationButton = signal<ButtonCreationEntity | undefined>(this._initial.creationButton);
  actionMenuItems = signal<ButtonActionEntity[] | undefined>(this._initial.actionMenuItems);
  linkedButton = signal<LinkedEntity | undefined>(this._initial.linkedButton);
  status = signal<StatusEntity | undefined>(this._initial.status);

  updateSubject<T extends ButtonCreationEntity | LinkedEntity | StatusEntity>(
    update: Partial<T>,
    subject: DETAIL_ACTION_SUBJECT,
  ) {
    const subjectMap: SubjectMapType = {
      CREATION_BUTTON: this.creationButton as WritableSignal<ButtonCreationEntity | undefined>,
      LINKED_BUTTON: this.linkedButton as WritableSignal<LinkedEntity | undefined>,
      STATUS_BUTTON: this.status as WritableSignal<StatusEntity | undefined>,
    };

    const targetSubject = subjectMap[subject] as WritableSignal<T | undefined>;

    if (targetSubject()) {
      targetSubject.update((current) => ({ ...current, ...update }) as T);
    }
  }

  setCreationButtonDisabledState(disabled: boolean) {
    if (this.creationButton()) {
      this.creationButton()!.disabled = disabled;
    }
  }

  clear(): void {
    const init = this._initial;
    this.creationButton.set(init.creationButton);
    this.actionMenuItems.set(init.actionMenuItems);
    this.linkedButton.set(init.linkedButton);
    this.status.set(init.status);
  }
}
